import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  // 30 s safety net. Cloudflare cuts at ~100 s — we'd rather show a fast error
  // and let the user retry than have the UI spin forever.
  timeout: 30_000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const activeStudio = localStorage.getItem("active_studio_id");
  if (activeStudio) {
    config.headers["X-Studio-Id"] = activeStudio;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("active_studio_id");
      localStorage.removeItem("active_studio");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

export const getActiveStudio = () => {
  try {
    const raw = localStorage.getItem("active_studio");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const getActiveStudioId = () => localStorage.getItem("active_studio_id");

// Role helpers
// Post-Phase-1: a Super Admin has role="admin" AND is_super_admin=true.
// We still recognise the legacy role="super_admin" for safety on edge cases.
export const isSuperAdmin = (user) => user?.is_super_admin === true || user?.role === "super_admin";
export const isAdmin = (user) => user?.role === "admin" || user?.role === "super_admin";

// Effective permissions: admins/super-admins ALWAYS get full access, regardless of
// any stale `permissions` object that may have been written to their record.
// Use this everywhere instead of reading `user.permissions` directly.
const FULL_PERMS = Object.freeze({
  attendance_access: "enabled",
  calendar_access: "edit",
  payment_visibility: "enabled",
  batch_edit_access: "enabled",
});
const INSTRUCTOR_DEFAULTS = Object.freeze({
  attendance_access: "enabled",
  calendar_access: "view",
  payment_visibility: "enabled",
  batch_edit_access: "disabled",
});
export const getEffectivePermissions = (user) => {
  if (isAdmin(user) || isSuperAdmin(user)) return FULL_PERMS;
  return { ...INSTRUCTOR_DEFAULTS, ...(user?.permissions || {}) };
};
export const isInstructor = (user) => user?.role === "instructor";
export const canEditPayment = (user) => isAdmin(user);
export const canManageCohorts = (user) => isAdmin(user);
export const canManageBatches = (user) => isAdmin(user);
export const canManagePricing = (user) => isAdmin(user);
export const canManageUsers = (user) => isAdmin(user);

export default api;
